<script>
import { required, email } from "@vuelidate/validators";

import Button from "@/components/atoms/button";
import FormInput from "@/components/molecules/form-input";
import FormGroup from "@/components/molecules/form-group";
import Form from "@/components/molecules/form";
import FormFieldset from "@/components/molecules/form-fieldset";
import Legend from "@/components/molecules/form-fieldset/legend";
import {
  formValidationProps,
  useFormValidation
} from "@/components/organisms/mixins/form-validation.mixin";
import StateOverlay from "@/components/molecules/state-overlay";
import MentionsLegalesCompte from "@/components/molecules/mentions-legales/compte";
import MentionsLegalesNewsletter from "@/components/molecules/mentions-legales/newsletter";

export default {
  name: "ModifierEmail",
  components: {
    Button,
    FormInput,
    FormGroup,
    Form,
    FormFieldset,
    Legend,
    StateOverlay,
    MentionsLegalesCompte,
    MentionsLegalesNewsletter
  },
  props: {
    isLoading: {
      type: Boolean,
      required: true
    },
    email: {
      type: String,
      required: true
    },
    hasAliasPaiement: {
      type: Boolean,
      required: false,
      default: false
    },
    ...formValidationProps()
  },
  emits: ["save"],
  setup(props) {
    const { v$, getState, getErrorMessage, checkValidation } =
      useFormValidation(props);
    return {
      v$,
      getState,
      getErrorMessage,
      checkValidation
    };
  },
  data() {
    return {
      model: {
        new_email: "",
        new_email_confirmation: ""
      }
    };
  },
  validations() {
    return {
      model: {
        new_email: {
          required,
          email
        },
        new_email_confirmation: {
          required,
          email,
          isValidConfirmation(value) {
            return this.model.new_email === value;
          }
        }
      }
    };
  },
  methods: {
    save() {
      if (this.checkValidation(this.v$)) {
        this.$emit("save", this.model.new_email);
      }
    }
  }
};
</script>

<template>
  <div>
    <h1 class="u-color-primary u-font-24 u-mb-md u-text-center">
      Modifier votre adresse email
    </h1>
    <div class="u-ml-xlg u-mr-xlg u-ml-sm@lg u-ml-sm@lg">
      <p>
        Votre nouvelle adresse email sera utilisée pour :
      </p>
      <ul>
        <li class="u-p-none">
          vous connecter aux sites internet et applications Cora
        </li>
        <li>
          recevoir les newsletters Cora
        </li>
      </ul>
      <p
        v-if="hasAliasPaiement"
        class="u-mt-sm"
      >
        Pour des raisons de sécurité cette opération va supprimer vos cartes bancaires enregistrées
      </p>
    </div>
    <Form
      class="u-mt-md"
      :error="error"
      @submit.prevent="save"
    >
      <StateOverlay
        :is-loading="isLoading"
        overlay
      >
        <FormFieldset
          center
          size="lg"
        >
          <Legend class="u-mb-sm">
            <span class="u-font-21">Votre adresse email actuelle</span>
          </Legend>
          <FormGroup
            center
            size="lg"
            state="success"
          >
            <FormInput
              size="lg"
              :model-value="email"
              disabled
              state="success"
            >
              Votre adresse email actuelle
            </FormInput>
          </FormGroup>
        </FormFieldset>
        <FormFieldset
          center
          size="lg"
          class="u-mb-md u-mt-md"
        >
          <Legend class="u-mb-sm">
            <span class="u-font-21">Votre nouvelle adresse email</span>
          </Legend>
          <FormGroup
            center
            class="u-mb-sm"
            size="lg"
            :state="getState(v$,'new_email', 'model')"
          >
            <FormInput
              v-model="model.new_email"
              size="lg"
              type="email"
              :state="getState(v$,'new_email', 'model')"
              @blur="v$.model.new_email.$touch()"
              @input="v$.model.new_email.$reset()"
            >
              Nouvelle adresse email
            </FormInput>
            <template #message>
              <p>
                <template v-if="v$.model.new_email.required.$invalid">
                  Merci de renseigner votre adresse email
                </template>
                <template v-else>
                  {{ getErrorMessage("new_email","Cette adresse email est invalide") }}
                </template>
              </p>
            </template>
          </FormGroup>
          <FormGroup
            center
            size="lg"
            :state="getState(v$,'new_email_confirmation', 'model')"
          >
            <FormInput
              v-model="model.new_email_confirmation"
              size="lg"
              type="email"
              :state="getState(v$,'new_email_confirmation', 'model')"
              @blur="v$.model.new_email_confirmation.$touch()"
              @input="v$.model.new_email_confirmation.$reset()"
            >
              Confirmez la nouvelle adresse email
            </FormInput>
            <template #message>
              <p>
                <template v-if="v$.model.new_email_confirmation.required.$invalid">
                  Merci de renseigner votre adresse email
                </template>
                <template v-else-if="v$.model.new_email_confirmation.email.$invalid">
                  Cette adresse email est invalide
                </template>
                <template v-else>
                  {{ getErrorMessage("new_email_confirmation","Cette adresse email n'est pas identique") }}
                </template>
              </p>
            </template>
          </FormGroup>
        </FormFieldset>
        <div class="u-text-center u-mt-lg u-mb-lg">
          <Button
            variant="primary"
            radius="full"
            size="md"
            type="submit"
            value="Mettre à jour mon adresse email"
          />
        </div>
      </StateOverlay>
    </Form>
    <MentionsLegalesCompte class="u-mt-lg" />
    <MentionsLegalesNewsletter class="u-mt-md" />
  </div>
</template>

<style lang="scss" scoped>
</style>
